import { useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useUserEndpoints } from '@/hooks/api/useUserEndpoints';
import { AgentVersionBadge } from '@/components/shared/AgentVersionBadge';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/date-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';

function Row({
  label,
  value,
}: {
  label: string;
  value: ReactElement | string;
}): ReactElement {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground flex-shrink-0">{label}</dt>
      <dd className="truncate text-right">{value}</dd>
    </div>
  );
}

interface EndpointDetailsPopoverProps {
  userId: string;
  machineId: string;
  endpointName?: string | null;
  /** Defaults to the route's :organizationId (Shadow-AI). Alerts pass it in. */
  organizationId?: string;
  /**
   * Overrides the trigger's width cap. Tables whose Endpoint column is narrower
   * than the 12rem default pass `max-w-full` so the label ellipsizes inside the
   * column instead of overflowing into the next one (KIR-4314).
   */
  triggerClassName?: string;
}

/**
 * KIR-3035 / KIR-3911: endpoint cell shared by the Shadow-AI tables and the
 * Alerts occurrences table. Shows the endpoint NAME (falling back to the raw
 * machine_id), and clicking opens a details box (OS / IP / MAC / agent version /
 * last seen), fetched from the endpoints registry by matching machine_id within
 * the user's endpoints. The endpoint id is plain text — the old link to
 * Inventory→Users was removed (KIR-3911); the agent version uses
 * {@link AgentVersionBadge} (red when older than the current release).
 */
export function EndpointDetailsPopover({
  userId,
  machineId,
  endpointName,
  organizationId: organizationIdProp,
  triggerClassName,
}: EndpointDetailsPopoverProps): ReactElement {
  const timeZone = useDisplayTimeZone();
  const params = useParams<{ organizationId: string }>();
  const organizationId = organizationIdProp ?? params.organizationId;
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useUserEndpoints(
    organizationId ?? '',
    userId,
    open && !!organizationId
  );
  const details = data?.endpoints.find(ep => ep.machineId === machineId);

  const label = details?.name || endpointName || machineId;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Endpoint details"
          onClick={e => e.stopPropagation()}
          className={cn(
            'block text-left underline decoration-dotted underline-offset-2 hover:text-foreground truncate max-w-[12rem]',
            triggerClassName
          )}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-3 text-xs"
        onClick={e => e.stopPropagation()}
      >
        <div className="font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Endpoint
        </div>
        <dl className="space-y-1">
          <Row label="Name" value={details?.name || endpointName || '—'} />
          <Row
            label="ID"
            value={<span className="font-mono">{machineId}</span>}
          />
          {isLoading && !details ? (
            <div className="pt-1 text-muted-foreground">Loading details…</div>
          ) : details ? (
            <>
              <Row label="OS" value={details.osString || '—'} />
              <Row
                label="IP"
                value={
                  details.ipAddresses.length
                    ? details.ipAddresses.join(', ')
                    : '—'
                }
              />
              <Row
                label="MAC"
                value={
                  details.macAddresses.length
                    ? details.macAddresses.join(', ')
                    : '—'
                }
              />
              <Row
                label="Agent version"
                value={<AgentVersionBadge version={details.agentVersion} />}
              />
              <Row
                label="Last seen"
                value={formatDateTime(details.lastSeen, timeZone)}
              />
            </>
          ) : (
            <div className="pt-1 text-muted-foreground">
              No further details recorded
            </div>
          )}
        </dl>
      </PopoverContent>
    </Popover>
  );
}

EndpointDetailsPopover.displayName = 'EndpointDetailsPopover';
